import moment from "moment";
import { getSalesByLevel3, getSalesSummaryCards } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

// Separador para claves compuestas (cuenta L3 + cliente) en `accountsData`
// y `accumulatedData` del shape jerárquico. Convención compartida con el
// use case `SalesReportUseCase` del backend — cambiar acá obliga a cambiar
// `HIERARCHICAL_KEY_SEPARATOR` en sales.py también.
const HIERARCHICAL_KEY_SEPARATOR = " :: ";

const state = () => ({
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["ene 2025", "feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por la clave de cada fila. En modo plano (cuentas/
     * categorías) la clave es el nombre de la cuenta level_3 (string simple).
     * En modo jerárquico (clientes), conviven dos tipos de claves:
     *  - Plana `<cuenta>`            → fila L2 agregada (suma de hijos).
     *  - Compuesta `<cuenta> :: <cliente>` → fila L3 individual.
     * Cada valor es: { [periodoLabel]: number }
     * @type {Object.<string, Object.<string, number>>}
     */
    accountsData: {},
    /** Lista ordenada de cuentas level_3 (modo plano). Vacío si es jerárquico. */
    accounts: [],
    /** Cuentas L3 padre (modo jerárquico clientes). Vacío en modo plano. */
    level2Accounts: [],
    /**
     * { cuenta L3 → [cliente1, cliente2, ...] } en modo jerárquico. Los hijos
     * vienen ya ordenados por monto descendente del backend. `{}` en modo plano.
     */
    hierarchy: {},
    /**
     * Acumulado del periodo + comparación contra ventana previa de igual largo.
     * Mismo keying que `accountsData` (planas y compuestas conviven) + clave
     * reservada `__total__` con el agregado global.
     * `pct` es null cuando prevAcc === 0 (frontend muestra "—").
     * @type {Object.<string, { acc: number, prevAcc: number, pct: number | null }>}
     */
    accumulatedData: {},
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Resumen para las summary cards: { current_month, ytd, ltm }.
     * Cada entrada incluye period_start, period_end, amount y ratios mom/yoy.
     * Se calcula independiente de los filtros de la tabla porque necesita
     * comparables que pueden quedar fuera del rango visible (LTM, YoY, etc.).
     */
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

const getters = {
    /**
     * True cuando la respuesta del backend trajo shape jerárquico (clientes
     * anidados bajo cuenta). Cambia el contrato de tableRows: en lugar de
     * filas planas vienen filas con `level: 2` (cuenta) intercaladas con sus
     * `level: 3` (clientes). El consumer filtra L3 por `expandedL2Rows`.
     */
    isHierarchical: (state) => state.level2Accounts.length > 0,

    /**
     * Construye las filas de la tabla a partir del shape recibido.
     *
     * Modo plano:    una fila por cuenta level_3.
     * Modo jerárquico: una fila L2 por cuenta + una L3 por cliente bajo esa
     *                  cuenta (en el orden de `hierarchy[cuenta]`). Cada L3
     *                  trae `parent` para filtrar visibilidad en el view.
     *
     * El acumulado y su % vs ventana previa van en la 2da columna sticky.
     * @returns {Array<{ label: string, level?: number, parent?: string, values: number[], accumulated: number, pct: number | null, isTotal: boolean }>}
     */
    tableRows: (state, getters) => {
        if (!getters.isHierarchical) {
            return state.accounts.map((account) => {
                const acc = state.accumulatedData[account] ?? EMPTY_ACC;
                return {
                    label: account,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[account]?.[header] ?? 0
                    ),
                    accumulated: acc.acc,
                    pct: acc.pct,
                };
            });
        }

        const rows = [];
        state.level2Accounts.forEach((parent) => {
            const l2Acc = state.accumulatedData[parent] ?? EMPTY_ACC;
            rows.push({
                label: parent,
                level: 2,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[parent]?.[header] ?? 0
                ),
                accumulated: l2Acc.acc,
                pct: l2Acc.pct,
            });

            const children = state.hierarchy[parent] || [];
            children.forEach((client) => {
                const compositeKey = `${parent}${HIERARCHICAL_KEY_SEPARATOR}${client}`;
                const l3Acc = state.accumulatedData[compositeKey] ?? EMPTY_ACC;
                rows.push({
                    label: client,
                    level: 3,
                    parent,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[compositeKey]?.[header] ?? 0
                    ),
                    accumulated: l3Acc.acc,
                    pct: l3Acc.pct,
                });
            });
        });
        return rows;
    },

    /**
     * Fila de totales: suma de todas las cuentas por período. El acumulado y
     * el pct vienen del backend (clave __total__) para que sean consistentes
     * con la query agregada del rango previo. La suma por período usa las
     * filas L2 en modo jerárquico (no las L3, para no contar dos veces a un
     * cliente que aparezca bajo dos cuentas distintas).
     * @returns {{ label: string, values: number[], accumulated: number, pct: number | null, isTotal: boolean }}
     */
    totalRow: (state, getters) => {
        const summableKeys = getters.isHierarchical
            ? state.level2Accounts
            : state.accounts;
        const values = state.monthHeaders.map((header) =>
            summableKeys.reduce(
                (sum, key) => sum + (state.accountsData[key]?.[header] ?? 0),
                0
            )
        );
        const totals = state.accumulatedData.__total__ ?? EMPTY_ACC;
        return {
            label: "Ventas Netas",
            values,
            isTotal: true,
            accumulated: totals.acc,
            pct: totals.pct,
        };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
    },
    SET_SALES_DATA(state, {
        accounts,
        level2Accounts,
        hierarchy,
        accountsData,
        monthHeaders,
        periodRange,
        accumulatedData,
    }) {
        state.accounts = accounts;
        state.level2Accounts = level2Accounts;
        state.hierarchy = hierarchy;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.accumulatedData = accumulatedData;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SUMMARY_CARDS(state, summary) {
        state.summaryCards = summary;
    },
    SET_SUMMARY_LOADING(state, isLoading) {
        state.isSummaryLoading = isLoading;
    },
    SET_SUMMARY_ERROR(state, error) {
        state.summaryError = error;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    // Bootstrap del tab Ventas — el view sólo llama esta action en `mounted`
    // y la action orquesta las dos lecturas (tabla + summary cards) en
    // paralelo. Cada una tiene su propia signature de refetch en watchers, así
    // que se mantienen exportadas individualmente para el watch-driven refresh.
    async initVentas({ dispatch }) {
        await Promise.all([
            dispatch("fetchSalesData"),
            dispatch("fetchSalesSummary"),
        ]);
    },

    async fetchSalesData({ state, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
            );

            // Format groupBy parameter (e.g. 'cuentas', 'clientes', or category name)
            const groupBy = state.activeSubFilter.toLowerCase();

            const response = await getSalesByLevel3(initDate, endDate, groupBy, filterPeriod);
            const {
                hierarchical,
                accounts,
                level2_accounts: level2AccountsRaw,
                hierarchy: hierarchyRaw,
                report_data: reportData,
                period_range: periodRange,
                accumulated_data: accumulatedRaw,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(
                periodRange.init_date,
                periodRange.end_date,
                filterPeriod
            );

            // El shape jerárquico (modo clientes) trae `level2_accounts` +
            // `hierarchy` y NO `accounts`. Las claves de `report_data` mezclan
            // L2 planas (`cuenta`) con L3 compuestas (`cuenta :: cliente`). El
            // parseo de períodos es el mismo para ambos shapes; solo cambia
            // qué llenamos en state.
            const isHierarchical = hierarchical === true;
            const dataKeys = isHierarchical
                ? Object.keys(reportData)
                : accounts;

            const accountsData = {};
            dataKeys.forEach((key) => {
                const row = {};
                (reportData[key] ?? []).forEach((item) => {
                    const label = formatPeriodLabel(item.period, filterPeriod);
                    row[label] = Number(item.total_amount);
                });
                // Freeze por fila: Vue 2 omite el observer recursivo en objetos congelados.
                accountsData[key] = Object.freeze(row);
            });

            // Normaliza la nomenclatura del backend (snake_case → camelCase) y
            // hace cast numérico explícito; el JSON puede serializar Decimal
            // como string en algunos paths del selector.
            const accumulatedData = {};
            Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                accumulatedData[key] = Object.freeze({
                    acc: Number(value?.acc ?? 0),
                    prevAcc: Number(value?.prev_acc ?? 0),
                    pct: value?.pct == null ? null : Number(value.pct),
                });
            });

            commit("SET_SALES_DATA", {
                accounts: Object.freeze(isHierarchical ? [] : accounts),
                level2Accounts: Object.freeze(
                    isHierarchical ? level2AccountsRaw : []
                ),
                hierarchy: Object.freeze(isHierarchical ? hierarchyRaw : {}),
                accountsData: Object.freeze(accountsData),
                monthHeaders: Object.freeze(monthHeaders),
                periodRange: Object.freeze(periodRange),
                accumulatedData: Object.freeze(accumulatedData),
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar ventas."));
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchSalesSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getSalesSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_SUMMARY_ERROR",
                extractErrorMessage(error, "Error al cargar resumen de ventas.")
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
